import { useEffect, useState, useCallback } from "react";
import api, { fmtDate, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { logoSrc } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Megaphone, Link as LinkIcon, Plus, Pencil, Trash2, Upload, FolderPlus, Users2 } from "lucide-react";
import { toast } from "sonner";
import { useCategories } from "@/components/CategoriesDialog";

const TYPES = [
  { v: "document", label: "Document", icon: FileText },
  { v: "toolbox", label: "Toolbox talk", icon: Megaphone },
  { v: "link", label: "Link", icon: LinkIcon },
];
const typeMeta = (t) => TYPES.find((x) => x.v === t) || TYPES[0];
const BLANK = { type: "document", title: "", description: "", category_id: "", file_url: null, filename: "", url: "", must_read: false, due_date: "", reconfirm: false, audience: { everyone: true, site_ids: [], role_names: [], category_names: [], employee_ids: [] } };

export default function Resources() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [sites, setSites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [staffCats] = useCategories();
  const [employees, setEmployees] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [uploading, setUploading] = useState(false);
  const [catDlg, setCatDlg] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [reads, setReads] = useState(null);

  const load = useCallback(() => {
    api.get("/resources").then((r) => setRows(r.data)).catch(() => {});
    api.get("/resource-categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    api.get("/job-roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [load]);

  const catName = (id) => cats.find((c) => c.id === id)?.name || "Uncategorised";

  const openCreate = () => { setEditId(null); setForm(BLANK); setDialog(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({ type: r.type, title: r.title || "", description: r.description || "", category_id: r.category_id || "",
      file_url: r.file_url || null, filename: r.filename || "", url: r.url || "", must_read: !!r.must_read, due_date: r.due_date || "", reconfirm: false,
      audience: { everyone: !!r.audience?.everyone, site_ids: r.audience?.site_ids || [], role_names: r.audience?.role_names || [], category_names: r.audience?.category_names || [], employee_ids: r.audience?.employee_ids || [] } });
    setDialog(true);
  };

  const upload = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, file_url: res.data.file_url, filename: f.name })); toast.success("File uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setUploading(false); }
  };

  const toggleArr = (key, val) => setForm((p) => {
    const arr = p.audience[key]; const next = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
    return { ...p, audience: { ...p.audience, [key]: next } };
  });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.type === "link" && !form.url.trim()) { toast.error("Enter a web address"); return; }
    if (form.type === "document" && !form.file_url) { toast.error("Upload a file"); return; }
    if (form.type === "toolbox" && !form.file_url && !form.url.trim()) { toast.error("Upload a file or enter a link"); return; }
    const payload = { ...form, url: form.url.trim() };
    try {
      if (editId) { await api.put(`/resources/${editId}`, payload); toast.success("Resource updated"); }
      else { await api.post("/resources", payload); toast.success("Draft saved"); }
      setDialog(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const publish = async (r) => {
    try { await api.post(`/resources/${r.id}/${r.status === "published" ? "unpublish" : "publish"}`); toast.success(r.status === "published" ? "Moved to draft" : "Published"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm("Delete this resource and its read records?")) return;
    try { await api.delete(`/resources/${r.id}`); toast.success("Resource deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openFile = (r) => { if (r.type === "link" || (!r.file_url && r.url)) { window.open(r.url, "_blank"); return; } const src = logoSrc(r.file_url); if (src) window.open(src, "_blank"); };
  const openReads = async (r) => { try { const { data } = await api.get(`/resources/${r.id}/reads`); setReads(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const addCat = async () => { if (!newCat.trim()) return; try { await api.post("/resource-categories", { name: newCat.trim() }); setNewCat(""); toast.success("Category added"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delCat = async (c) => { try { await api.delete(`/resource-categories/${c.id}`); toast.success("Category deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const filtered = rows.filter((r) => (catFilter === "all" || r.category_id === catFilter || (catFilter === "none" && !r.category_id)) && (typeFilter === "all" || r.type === typeFilter));

  return (
    <AdminLayout nav={adminNav} title="Resources">
      <PageHeader title="Resource Library" subtitle="Publish documents, toolbox talks and links for your staff">
        <div className="flex gap-2">
          <Button data-testid="manage-categories-btn" variant="outline" onClick={() => setCatDlg(true)}><FolderPlus className="h-4 w-4 mr-1" />Categories</Button>
          <Button data-testid="add-resource-btn" onClick={openCreate} className="bg-slate-900 text-white"><Plus className="h-4 w-4 mr-1" />Add resource</Button>
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden text-sm">
          {[["all", "All types"], ...TYPES.map((t) => [t.v, t.label])].map(([v, l]) => (
            <button key={v} type="button" data-testid={`type-filter-${v}`} onClick={() => setTypeFilter(v)} className={`px-3 py-1.5 ${typeFilter === v ? "bg-slate-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          <button type="button" data-testid="cat-filter-all" onClick={() => setCatFilter("all")} className={`text-xs px-2.5 py-1 rounded-full border ${catFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-zinc-600 border-zinc-200"}`}>All categories</button>
          {cats.map((c) => (
            <button key={c.id} type="button" data-testid={`cat-filter-${c.id}`} onClick={() => setCatFilter(c.id)} className={`text-xs px-2.5 py-1 rounded-full border ${catFilter === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-zinc-600 border-zinc-200"}`}>{c.name}</button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Title</th><th className="p-3">Type</th><th className="p-3">Category</th><th className="p-3">Audience</th><th className="p-3">Status</th><th className="p-3 text-right">Read</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No resources yet — add your first one.</td></tr>}
            {filtered.map((r) => { const TM = typeMeta(r.type); return (
              <tr key={r.id} data-testid={`resource-row-${r.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                <td className="p-3 font-medium"><button className="text-left hover:underline" data-testid={`resource-open-${r.id}`} onClick={() => openFile(r)}>{r.title}</button>{r.must_read && <span data-testid={`resource-mustread-${r.id}`} className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Must read{r.due_date ? ` · due ${fmtDate(r.due_date)}` : ""}</span>}{r.description && <div className="text-xs text-zinc-400 font-normal">{r.description}</div>}</td>
                <td className="p-3"><span className="inline-flex items-center gap-1 text-xs"><TM.icon className="h-3.5 w-3.5" />{TM.label}</span></td>
                <td className="p-3"><span className="text-xs bg-zinc-100 px-2 py-0.5 rounded">{catName(r.category_id)}</span></td>
                <td className="p-3 text-xs text-zinc-500">{r.audience_summary}</td>
                <td className="p-3"><span data-testid={`resource-status-${r.id}`} className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{r.status}</span></td>
                <td className="p-3 text-right"><button data-testid={`resource-reads-${r.id}`} onClick={() => openReads(r)} className="text-xs text-amber-600 hover:underline inline-flex items-center gap-1"><Users2 className="h-3.5 w-3.5" />{r.read_count}</button></td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button data-testid={`resource-publish-${r.id}`} size="sm" variant="outline" className="h-8" onClick={() => publish(r)}>{r.status === "published" ? "Unpublish" : "Publish"}</Button>
                    <Button data-testid={`resource-edit-${r.id}`} size="sm" variant="outline" className="h-8" onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`resource-delete-${r.id}`} size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200" onClick={() => del(r)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto kqm-scroll">
          <DialogHeader><DialogTitle>{editId ? "Edit resource" : "Add resource"}</DialogTitle><DialogDescription>Set the type, details and who can see it. It stays a draft until you publish.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <div className="flex gap-2 mt-1">
                {TYPES.map((t) => (
                  <button key={t.v} type="button" data-testid={`rtype-${t.v}`} onClick={() => setForm({ ...form, type: t.v })} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md border text-sm ${form.type === t.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-zinc-600 border-zinc-200"}`}><t.icon className="h-4 w-4" />{t.label}</button>
                ))}
              </div>
            </div>
            <div><Label>Title</Label><Input data-testid="r-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
            <div><Label>Description (optional)</Label><Textarea data-testid="r-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <div><Label>Category</Label>
              <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="r-category" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Uncategorised</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.type !== "link" && (
              <div>
                <Label>File {form.type === "toolbox" ? "(or use a link below)" : ""}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input id="r-file" type="file" data-testid="r-file" className="hidden" onChange={upload} accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" />
                  <Button type="button" variant="outline" onClick={() => document.getElementById("r-file").click()} disabled={uploading}><Upload className="h-4 w-4 mr-1" />{uploading ? "Uploading…" : "Upload file"}</Button>
                  {form.filename && <span className="text-xs text-zinc-500 truncate" data-testid="r-filename">{form.filename}</span>}
                </div>
              </div>
            )}
            {form.type !== "document" && (
              <div><Label>{form.type === "link" ? "Web address" : "Link (optional)"}</Label><Input data-testid="r-url" placeholder="https://…" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1" /></div>
            )}
            <div className="border-t border-zinc-100 pt-3 space-y-2">
              <label className="flex items-start gap-2 text-sm cursor-pointer"><Checkbox data-testid="r-must-read" checked={form.must_read} onCheckedChange={(v) => setForm({ ...form, must_read: !!v })} className="mt-0.5" /><span>Must read<span className="block text-xs text-zinc-500">Staff tick "I have read and understood" and can't submit timesheets until they do</span></span></label>
              {form.must_read && <div className="pl-6"><Label>Deadline (optional)</Label><Input data-testid="r-due-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 w-44" /></div>}
              {form.must_read && editId && <label className="flex items-start gap-2 text-sm cursor-pointer pl-6"><Checkbox data-testid="r-reconfirm" checked={form.reconfirm} onCheckedChange={(v) => setForm({ ...form, reconfirm: !!v })} className="mt-0.5" />Ask everyone to confirm again on save</label>}
            </div>
            <div className="border-t border-zinc-100 pt-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid="aud-everyone" checked={form.audience.everyone} onCheckedChange={(v) => setForm({ ...form, audience: { ...form.audience, everyone: !!v } })} />Everyone in the company</label>
              {!form.audience.everyone && (
                <div className="mt-3 space-y-3">
                  <AudienceBox testid="aud-sites" title="Sites" items={sites.map((s) => [s.id, s.name])} selected={form.audience.site_ids} onToggle={(v) => toggleArr("site_ids", v)} />
                  <AudienceBox testid="aud-roles" title="Roles" items={roles.map((r) => [r.name, r.name])} selected={form.audience.role_names} onToggle={(v) => toggleArr("role_names", v)} />
                  <AudienceBox testid="aud-cats" title="Staff categories" items={staffCats.map((c) => [c.name, c.name])} selected={form.audience.category_names || []} onToggle={(v) => toggleArr("category_names", v)} />
                  <AudienceBox testid="aud-people" title="Individuals" items={employees.map((e) => [e.id, `${e.first_name} ${e.surname}`])} selected={form.audience.employee_ids} onToggle={(v) => toggleArr("employee_ids", v)} />
                </div>
              )}
            </div>
            <Button data-testid="r-save" onClick={save} className="w-full bg-slate-900 text-white">{editId ? "Save changes" : "Save draft"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={catDlg} onOpenChange={setCatDlg}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Categories</DialogTitle><DialogDescription>Name the groups staff browse by. Empty categories can be deleted.</DialogDescription></DialogHeader>
          <div className="flex gap-2"><Input data-testid="new-cat-name" placeholder="e.g. Safety" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} /><Button data-testid="add-cat-btn" onClick={addCat} className="bg-slate-900 text-white">Add</Button></div>
          <div className="space-y-1 mt-2 max-h-60 overflow-y-auto kqm-scroll">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center justify-between border-b border-zinc-50 py-1.5 text-sm"><span>{c.name}</span><Button data-testid={`del-cat-${c.id}`} size="sm" variant="outline" className="h-7 text-rose-600 border-rose-200" onClick={() => delCat(c)}><Trash2 className="h-3.5 w-3.5" /></Button></div>
            ))}
            {cats.length === 0 && <div className="text-xs text-zinc-400 py-2">No categories yet.</div>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reads} onOpenChange={(o) => !o && setReads(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto kqm-scroll" data-testid="reads-dialog">
          <DialogHeader><DialogTitle>{reads?.title}</DialogTitle><DialogDescription>{reads?.read_count} of {reads?.targeted_count} targeted people have {reads?.must_read ? "confirmed" : "read"} this.{reads?.due_date ? ` Due ${fmtDate(reads.due_date)}.` : ""}{reads?.overdue_count ? ` ${reads.overdue_count} overdue.` : ""}</DialogDescription></DialogHeader>
          <div className="space-y-1">
            {reads?.people?.map((p) => (
              <div key={p.employee_id} className="flex items-center justify-between border-b border-zinc-50 py-1.5 text-sm">
                <span>{p.name}<span className="text-xs text-zinc-400 ml-1">{p.grade}</span></span>
                {p.read ? <span className="text-xs text-emerald-600 font-semibold">Read {p.read_at ? fmtDate(p.read_at) : ""}</span> : <span data-testid={`reads-status-${p.employee_id}`} className={`text-xs ${p.overdue ? "text-rose-600 font-semibold" : "text-zinc-400"}`}>{p.overdue ? "Overdue" : p.needs_reconfirm ? "Needs to re-confirm" : "Not yet"}</span>}
              </div>
            ))}
            {(reads?.people?.length ?? 0) === 0 && <div className="text-xs text-zinc-400 py-2">No one is targeted yet.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function AudienceBox({ testid, title, items, selected, onToggle }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">{title}</div>
      <div className="max-h-32 overflow-y-auto border border-zinc-200 rounded-md p-2 space-y-1 kqm-scroll">
        {items.length === 0 && <div className="text-xs text-zinc-400">None available</div>}
        {items.map(([val, label]) => (
          <label key={val} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid={`${testid}-opt`} checked={selected.includes(val)} onCheckedChange={() => onToggle(val)} />{label}</label>
        ))}
      </div>
    </div>
  );
}
